import { TrackEventTypes } from '@openeducation/oe-rn-shared-types';
import { oeChartColors, styled } from '@openeducation/oe-rn-themes';
import { format } from 'date-fns';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import type { StyleProp, TouchableOpacityProps, ViewStyle } from 'react-native';
import { useTheme } from 'react-native-paper';
import Icon from 'react-native-vector-icons/FontAwesome5';

import levelBadgeBlockedImg from '../../assets/progress-page/medal-level-badge-blocked.png';
import levelBadgeImg from '../../assets/progress-page/medal-level-badge.png';
import NoPointerEventsView from '../../atoms/no-pointer-events-view/no-pointer-events-view';
import { OEText } from '../../atoms/oe-text/oe-text';
import { flex, ml } from '../../style-utils';

export interface CertificateCardLabels {
  certificate: string;
  of: string;
  completion: string;
  proficiency: string;
  blocked: string;
  buttonShare: string;
  buttonDownload: string;
}
export interface CertificateCardProps {
  level: number;
  date?: Date | null;
  blocked: boolean;
  labels: CertificateCardLabels;
  downloadAction?: () => void;
  shareAction?: () => void;
  style?: StyleProp<ViewStyle>;
  blockedTextSize?: number;
}

export function CertificateCard({
  level,
  date,
  blocked,
  downloadAction,
  shareAction,
  style,
  labels,
  blockedTextSize,
}: CertificateCardProps) {
  const { colors } = useTheme();

  const isOdd = level % 2 !== 0;
  const textColor = blocked ? colors.dark1 : colors.white;

  return (
    <Container style={style}>
      <BadgeContainer
        source={blocked ? levelBadgeBlockedImg : levelBadgeImg}
        style={[flex('align-items-center', 'justify-content-center')]}
      >
        <LevelNum>{level}</LevelNum>
      </BadgeContainer>

      <TitleContainer
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        colors={
          blocked
            ? [colors.light1, colors.light1]
            : [colors.primary, colors.primaryDark]
        }
      >
        <TextContainer>
          <CertificateText color={textColor}>
            {labels.certificate}
          </CertificateText>
          <CompletionText color={textColor}>
            {labels.of} {isOdd ? labels.completion : labels.proficiency}
          </CompletionText>
        </TextContainer>
      </TitleContainer>

      <DateContainer>
        {date && <DateText>{format(date, "dd MMM',' yyyy")}</DateText>}
      </DateContainer>

      <ActionContainer>
        {blocked ? (
          <ActionText fontSize={blockedTextSize} color={colors.dark1}>
            {labels.blocked}
          </ActionText>
        ) : (
          <>
            {shareAction ? (
              <ActionButton
                first
                onPress={shareAction}
                nativeID={TrackEventTypes.SOCIAL_SHARING_START_PROGRESS}
              >
                <ActionButtonContent>
                  <ActionText>{labels.buttonShare}</ActionText>
                  <Icon
                    name="share-alt"
                    size={10}
                    color={oeChartColors.oeBlue}
                    style={ml(4)}
                  />
                </ActionButtonContent>
              </ActionButton>
            ) : null}
            {downloadAction ? (
              <ActionButton onPress={downloadAction}>
                <ActionButtonContent>
                  <ActionText>{labels.buttonDownload}</ActionText>
                  <Icon
                    name="download"
                    size={10}
                    color={oeChartColors.oeBlue}
                    style={ml(4)}
                  />
                </ActionButtonContent>
              </ActionButton>
            ) : null}
          </>
        )}
      </ActionContainer>
    </Container>
  );
}

const Container = styled.View`
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.color.backgroundOeBlue20};
  padding-top: 24px;
  background-color: ${({ theme }) => theme.color.white};
  min-width: 256px;
  position: relative;
`;

const TitleContainer = styled(LinearGradient)`
  height: 72px;
  width: 100%;
  flex-direction: row;
  align-items: center;
  padding: 0 16px 0 8px;
`;

const BadgeContainer = styled.ImageBackground`
  position: absolute;
  width: 112px;
  height: 112px;
  top: 8px;
  left: 8px;
  z-index: 10;
`;

const TextContainer = styled.View`
  position: absolute;
  width: 50%;
  right: 0;
`;

const CertificateText = styled(OEText).attrs(({ theme }) => ({
  fontFamily: theme.secondaryFont,
  fontType: 'regular',
  fontSize: 20,
}))``;

const CompletionText = styled(OEText).attrs(({ theme }) => ({
  fontFamily: theme.secondaryFont,
  fontType: 'thin',
  fontSize: 16,
}))``;

const LevelNum = styled(OEText).attrs(({ theme }) => ({
  fontSize: 54,
  color: theme.color.white,
  fontFamily: theme.font,
  fontType: 'bold',
}))`
  margin-top: 5px;
`;

const DateContainer = styled.View`
  align-self: flex-end;
  justify-content: center;
  width: 50%;
  height: 48px;
`;

const DateText = styled(OEText).attrs(({ theme }) => ({
  fontFamily: theme.secondaryFont,
  fontType: 'regular',
  fontSize: 16,
}))``;

const ActionContainer = styled.View`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  border: 0 solid ${({ theme }) => theme.color.light1};
  border-top-width: 1px;
`;

const ActionText = styled(OEText).attrs(({ theme, fontSize }) => ({
  fontFamily: theme.secondaryFont,
  fontType: 'regular',
  fontSize: fontSize || 12,
}))`
  padding: 8px 0;
`;

const ActionButton = styled.TouchableOpacity.attrs((props) => ({
  nativeID: (props as TouchableOpacityProps & { nativeID?: string }).nativeID,
  activeOpacity: 0.8,
}))<{ first?: boolean }>`
  align-items: center;
  border: 0 solid ${({ theme }) => theme.color.light1};
  border-right-width: ${({ first }) => (first ? 1 : 0)}px;
  text-align: center;
  flex: 1;
`;

const ActionButtonContent = styled(NoPointerEventsView)`
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;
